// src/components/BuyNumberPanel.js
import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  useToast,
  VStack,
  Tooltip,
  IconButton,
  Fade,
  useColorMode,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { useWalletClient, useAccount } from "wagmi";
import { apiFetch } from "../utils/api";
import { sendUsdtPayment } from "../utils/sendUsdtPayment";
import EmailModal from "./EmailModal";

// Freeze purchases during the daily settlement window
function useNoonFreeze() {
  const [frozen, setFrozen] = useState(false);

  useEffect(() => {
    const compute = () => {
      const now = new Date();
      const h = now.getUTCHours();
      const m = now.getUTCMinutes();

      // 20:50–20:59 OR 21:00–21:10 UTC
      const isFrozen = (h === 20 && m >= 50) || (h === 21 && m <= 10);

      setFrozen(isFrozen);
    };

    compute();

    const id = setInterval(compute, 15_000);

    return () => clearInterval(id);
  }, []);

  return frozen;
}

const BuyNumberPanel = () => {
  const { address, isConnected } = useAccount();

  const walletAddress = address ? address.toLowerCase() : null;

  const [quantity, setQuantity] = useState(1);

  const [quantityInput, setQuantityInput] = useState("1");

  const [sliderHover, setSliderHover] = useState(false);

  const [purchasedNumbers, setPurchasedNumbers] = useState([]);

  const [winnerNumbers, setWinnerNumbers] = useState([]);

  const containerRef = useRef(null);

  const [isMobile, setIsMobile] = useState(false);

  const MAX_DAILY_PURCHASE = 10000;
  const DAILY_GLOBAL_CAP = 1_000_000;

  const [loading, setLoading] = useState(false);

  const { data: walletClient } = useWalletClient();

  const toast = useToast();
  const { colorMode } = useColorMode();
  const isFreeze = useNoonFreeze();

  const [purchasesToday, setPurchasesToday] = useState(0);

  const holdInterval = useRef();

  const [userEmail, setUserEmail] = useState(null);

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const [emailChecked, setEmailChecked] = useState(false);

  const [emailPromptShown, setEmailPromptShown] = useState(false);

  useEffect(() => {
    if (isConnected && emailChecked && !userEmail && !emailPromptShown) {
      setIsEmailModalOpen(true);
      setEmailPromptShown(true);
    }
  }, [isConnected, emailChecked, userEmail, emailPromptShown]);

  const remainingQuota = React.useMemo(() => {
    return Math.max(0, MAX_DAILY_PURCHASE - purchasesToday);
  }, [purchasesToday]);

  const fetchTodaysNumbers = async () => {
    if (!walletAddress) {
      setPurchasedNumbers([]);
      setWinnerNumbers([]);
      setPurchasesToday(0);
      setUserEmail(null);
      setEmailChecked(false);

      return 0;
    }

    try {
      const account = await apiFetch(
        `/app/account?wallet=${encodeURIComponent(walletAddress)}`,
      );

      const registered = Boolean(account?.registered && account?.user);

      const hasEmail = Boolean(account?.user?.hasEmail);

      const purchases = Array.isArray(account?.purchases)
        ? account.purchases
        : [];

      const winners = Array.isArray(account?.winners) ? account.winners : [];

      const purchased = purchases.map((purchase) => String(purchase.number));

      const winnersList = winners.map((winner) => String(winner.number));

      const count = Number.isFinite(Number(account?.purchasesToday))
        ? Number(account.purchasesToday)
        : purchased.length;

      setUserEmail(registered && hasEmail ? "registered" : null);

      setEmailChecked(true);
      setPurchasedNumbers(purchased);
      setWinnerNumbers(winnersList);
      setPurchasesToday(count);

      return count;
    } catch (error) {
      console.warn("Failed to fetch account tickets:", error?.message || error);

      setEmailChecked(true);

      return purchasesToday;
    }
  };

  useEffect(() => {
    fetchTodaysNumbers();
  }, [walletAddress]);

  useEffect(() => {
    if (quantity > remainingQuota) {
      const clamped = Math.max(1, remainingQuota);

      setQuantity(clamped);
      setQuantityInput(String(clamped));
    }
  }, [purchasesToday, remainingQuota, quantity]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);

    check();

    window.addEventListener("resize", check);

    return () => window.removeEventListener("resize", check);
  }, []);

  const handleBuy = async () => {
    if (!isConnected) {
      return toast({
        title: "Connect your wallet first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    if (!walletClient) {
      return toast({
        title: "Wallet Not Ready",
        description: "Reconnect your wallet.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    if (isFreeze) {
      return toast({
        title: "Purchases Paused",
        description:
          "Daily settlement in progress. Please try again in a few minutes.",
        status: "info",
        duration: 6000,
        isClosable: true,
      });
    }

    if (!userEmail) {
      setIsEmailModalOpen(true);
      return;
    }

    if (purchasesToday >= MAX_DAILY_PURCHASE) {
      return toast({
        title: "Daily Limit Reached",
        description: `You’ve reached the ${MAX_DAILY_PURCHASE}-ticket daily limit.`,
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    }

    try {
      const state = await apiFetch("/app/state");

      const issuedToday = Number(state?.ticketsIssued || 0);

      const globalCap = Number(state?.globalTicketCap || DAILY_GLOBAL_CAP);

      if (issuedToday >= globalCap) {
        return toast({
          title: "Ticket Sales Closed",
          description:
            `Today’s global cap of ${globalCap.toLocaleString()} tickets has been reached. ` +
            "Sales will reopen after the daily reset.",
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
      }

      const remainingGlobal = Math.max(globalCap - issuedToday, 0);

      if (remainingGlobal > 0 && Number(quantity) > remainingGlobal) {
        setQuantity(remainingGlobal);

        setQuantityInput(String(remainingGlobal));

        toast({
          title: "Quantity adjusted",
          description:
            `Only ${remainingGlobal.toLocaleString()} tickets remain ` +
            "before today’s global cap.",
          status: "info",
          duration: 6000,
          isClosable: true,
        });

        return;
      }
    } catch (error) {
      console.warn("Global cap pre-check failed:", error?.message || error);
    }

    try {
      setLoading(true);

      const timeout = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("⏱ Transaction timed out. Try again.")),
          90000,
        ),
      );

      await Promise.race([
        sendUsdtPayment({
          walletClient,
          walletAddress,
          quantity,
        }),
        timeout,
      ]);

      toast({
        title: "Payment Sent",
        description: "Waiting for auto-verification...",
        status: "info",
        duration: 4000,
        isClosable: true,
      });

      // Poll the backend until the
      // reconciler creates the tickets.
      let confirmed = false;

      const maxAttempts = 12;
      const delay = 5000;

      for (let i = 0; i < maxAttempts; i += 1) {
        const latestCount = await fetchTodaysNumbers();

        const expectedCount = purchasesToday + quantity;

        if (latestCount >= expectedCount) {
          confirmed = true;
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      if (!confirmed) {
        return toast({
          title: "Tickets Not Detected",
          description:
            "Your payment was sent but tickets didn’t appear within the expected window. " +
            `If today’s global cap of ${DAILY_GLOBAL_CAP.toLocaleString()} tickets was reached, ` +
            "your purchase may not issue new tickets. Please check again after 21:00 UTC.",
          status: "warning",
          duration: 9000,
          isClosable: true,
        });
      }

      toast({
        title: "Purchase successful!",
        description: `You bought ${quantity} ticket${quantity > 1 ? "s" : ""}.`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong.";

      if (
        message.includes("global daily cap") ||
        message.includes("Ticket sales are closed") ||
        error?.response?.data?.code === "DAILY_CAP_REACHED"
      ) {
        toast({
          title: "Ticket Sales Closed",
          description:
            `Today’s global cap of ${DAILY_GLOBAL_CAP.toLocaleString()} tickets has been reached. ` +
            "Sales will reopen after the daily reset.",
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;

    // Allow empty string while typing.
    if (value === "") {
      setQuantityInput("");
      return;
    }

    if (/^\d*$/.test(value)) {
      setQuantityInput(value);
    }
  };

  const handleInputBlur = () => {
    const parsed = Number.parseInt(quantityInput, 10);

    if (Number.isNaN(parsed) || parsed < 1) {
      setQuantity(1);
      setQuantityInput("1");
    } else {
      const clamped = Math.min(parsed, remainingQuota);

      setQuantity(clamped);

      setQuantityInput(String(clamped));
    }
  };

  const increment = () =>
    setQuantity((currentQuantity) => {
      const next = Math.min(remainingQuota, Number(currentQuantity) + 1);

      setQuantityInput(String(next));

      return next;
    });

  const decrement = () =>
    setQuantity((currentQuantity) => {
      const next = Math.max(1, Number(currentQuantity) - 1);

      setQuantityInput(String(next));

      return next;
    });

  const handleCopyNumber = (number) => {
    navigator.clipboard
      .writeText(number)
      .then(() => {
        toast({
          title: "Number Copied",
          description: `Copied ${number} to clipboard.`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      })
      .catch(() => {
        toast({
          title: "Copy Failed",
          status: "error",
          duration: 2000,
          isClosable: true,
        });
      });
  };

  const [showAllNumbers, setShowAllNumbers] = useState(false);

  const PAGE_SIZE = 1000;

  const [currentPage, setCurrentPage] = useState(1);

  const visibleNumbers = useMemo(() => {
    if (!showAllNumbers) {
      return isMobile
        ? purchasedNumbers.slice(0, 7)
        : purchasedNumbers.slice(0, 49);
    }

    const start = (currentPage - 1) * PAGE_SIZE;

    const end = start + PAGE_SIZE;

    return purchasedNumbers.slice(start, end);
  }, [purchasedNumbers, showAllNumbers, currentPage, isMobile]);

  return (
    <VStack spacing={6}>
      {purchasesToday < MAX_DAILY_PURCHASE ? (
        <>
          <Flex direction="column" width="100%" align="center" gap={4}>
            <Flex align="center" gap={2}>
              <Text>Quantity:</Text>

              <IconButton
                icon={<MinusIcon />}
                size="xs"
                onMouseDown={() => {
                  decrement();

                  holdInterval.current = setInterval(decrement, 150);
                }}
                onMouseUp={() => clearInterval(holdInterval.current)}
                onMouseLeave={() => clearInterval(holdInterval.current)}
                aria-label="Decrease"
                isDisabled={isFreeze || remainingQuota === 0}
              />

              <Input
                type="number"
                value={quantityInput}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                min={1}
                max={remainingQuota}
                width="80px"
                textAlign="center"
                isDisabled={isFreeze || remainingQuota === 0}
              />

              <IconButton
                icon={<AddIcon />}
                size="xs"
                onMouseDown={() => {
                  increment();

                  holdInterval.current = setInterval(increment, 150);
                }}
                onMouseUp={() => clearInterval(holdInterval.current)}
                onMouseLeave={() => clearInterval(holdInterval.current)}
                aria-label="Increase"
                isDisabled={isFreeze || remainingQuota === 0}
              />
            </Flex>

            <Box
              width={{
                base: "95%",
                sm: "75%",
                md: "60%",
              }}
            >
              <Slider
                value={quantity}
                min={1}
                max={remainingQuota}
                step={1}
                onChange={(value) => {
                  setQuantity(value);

                  setQuantityInput(String(value));
                }}
                onMouseEnter={() => setSliderHover(true)}
                onMouseLeave={() => setSliderHover(false)}
                isDisabled={isFreeze || remainingQuota === 0}
              >
                <SliderTrack
                  bg={colorMode === "light" ? "gray.400" : "gray.700"}
                >
                  <SliderFilledTrack bg="teal.400" />
                </SliderTrack>

                <Tooltip
                  hasArrow
                  bg="teal.500"
                  color="white"
                  placement="top"
                  isOpen={sliderHover}
                  label={`${quantity}`}
                >
                  <SliderThumb />
                </Tooltip>
              </Slider>
            </Box>
          </Flex>

          <Button
            onClick={handleBuy}
            isLoading={loading}
            size="md"
            isDisabled={
              isFreeze ||
              loading ||
              !userEmail ||
              purchasesToday >= MAX_DAILY_PURCHASE ||
              quantity > remainingQuota
            }
            bg={colorMode === "light" ? "black" : "white"}
            color={colorMode === "light" ? "white" : "black"}
            _hover={{
              bg: colorMode === "light" ? "gray.800" : "gray.200",
            }}
          >
            {isFreeze ? (
              "Paused for daily settlement…"
            ) : (
              <>
                Enter Lottery 👉 X{quantity}
                {quantity > 99 ? " 🚀" : ""}
              </>
            )}
          </Button>

          {isFreeze && (
            <Alert status="info" width="100%">
              <AlertIcon />
              Purchases are paused for daily settlement. Please try again
              shortly.
            </Alert>
          )}

          {!userEmail && (
            <Text fontSize="sm" color="red.400" mt={1}>
              Please provide your email before buying tickets.
            </Text>
          )}

          <Fade in>
            <Text fontSize="sm" color="gray.500" mt={2}>
              🎟️ {MAX_DAILY_PURCHASE - purchasesToday} tickets left today
            </Text>
          </Fade>
        </>
      ) : (
        <VStack spacing={1} mt={4}>
          <Text color="green.500" fontWeight="semibold">
            🎉 You hit today’s {MAX_DAILY_PURCHASE}
            -ticket limit!
          </Text>
        </VStack>
      )}

      {purchasedNumbers.length > 0 && (
        <Box mt={4} width="100%" ref={containerRef}>
          <Text fontWeight="semibold" mb={2}>
            Your Numbers Today:
          </Text>

          <Flex wrap="wrap" gap={2}>
            {visibleNumbers.map((number, index) => (
              <Box
                key={index}
                p={2}
                border="1px solid"
                borderColor={
                  winnerNumbers.includes(String(number)) ? "green.400" : "#ccc"
                }
                color={
                  winnerNumbers.includes(String(number))
                    ? "green.400"
                    : "inherit"
                }
                borderRadius="md"
                cursor="pointer"
                onClick={() => handleCopyNumber(number)}
                _hover={{
                  bg: "gray.100",
                  color: "black",
                }}
              >
                {number}
              </Box>
            ))}

            {!showAllNumbers &&
              ((isMobile && purchasedNumbers.length > 7) ||
                (!isMobile && purchasedNumbers.length > 50)) && (
                <Box
                  p={2}
                  border="1px solid #ccc"
                  borderRadius="md"
                  minW="56px"
                  textAlign="center"
                  cursor="pointer"
                  onClick={() => {
                    setShowAllNumbers(true);

                    setTimeout(() => {
                      window.scrollBy({
                        top: 150,
                        behavior: "smooth",
                      });
                    }, 100);
                  }}
                >
                  More ...
                </Box>
              )}
          </Flex>

          {showAllNumbers && purchasedNumbers.length > PAGE_SIZE && (
            <Flex mt={4} justify="center" gap={2} flexWrap="wrap">
              <Button
                size="sm"
                onClick={() =>
                  setCurrentPage((previousPage) =>
                    Math.max(1, previousPage - 1),
                  )
                }
                isDisabled={currentPage === 1}
              >
                Previous
              </Button>

              <Button
                size="sm"
                onClick={() =>
                  setCurrentPage((previousPage) =>
                    previousPage <
                    Math.ceil(purchasedNumbers.length / PAGE_SIZE)
                      ? previousPage + 1
                      : previousPage,
                  )
                }
                isDisabled={
                  currentPage >= Math.ceil(purchasedNumbers.length / PAGE_SIZE)
                }
              >
                Next
              </Button>
            </Flex>
          )}
        </Box>
      )}

      <EmailModal
        walletAddress={walletAddress}
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onRegistered={() => {
          fetchTodaysNumbers();

          setIsEmailModalOpen(false);
        }}
      />
    </VStack>
  );
};

export default BuyNumberPanel;
